/**
 * Roda `fn` quando o navegador estiver ocioso, e nunca antes do evento `load`.
 *
 * Existe para tirar trabalho não essencial do caminho crítico da primeira
 * pintura — telemetria, seções abaixo da dobra, qualquer coisa que a página não
 * precisa ter pronta para ser útil.
 *
 * O `timeout` do `requestIdleCallback` evita o adiamento indefinido numa aba
 * que nunca fica realmente ociosa; o `setTimeout` cobre navegadores sem
 * `requestIdleCallback` (Safari só passou a suportá-lo recentemente).
 *
 * Devolve uma função de cancelamento, para uso direto no cleanup de um efeito.
 */
export function scheduleIdle(fn: () => void, timeoutMs = 4000): () => void {
  if (typeof window === "undefined") {
    return () => {};
  }

  let cancelled = false;
  let cancelInner: () => void = () => {};

  const schedule = () => {
    if (cancelled) return;

    const ric = (
      window as typeof window & {
        requestIdleCallback?: (
          cb: () => void,
          opts?: { timeout: number },
        ) => number;
      }
    ).requestIdleCallback;

    if (typeof ric === "function") {
      const id = ric(() => !cancelled && fn(), { timeout: timeoutMs });
      cancelInner = () => window.cancelIdleCallback?.(id);
      return;
    }

    const id = window.setTimeout(() => !cancelled && fn(), 1200);
    cancelInner = () => window.clearTimeout(id);
  };

  if (document.readyState === "complete") {
    schedule();
  } else {
    window.addEventListener("load", schedule, { once: true });
    cancelInner = () => window.removeEventListener("load", schedule);
  }

  return () => {
    cancelled = true;
    cancelInner();
  };
}
